<template>
  <div class="page-wrapper v-cloak">
    <div class="page-grid-wrapper">
      <div class="page-grid-fix">
        <div class="space-helper"></div>
        <h2><img class="rm-logo" src="/images/RM-logo-white.svg" /></h2>
        <h4 style="width: 400px;">{{ siteSettings.rm_watches_lead }}</h4>

        <!-- FILTERS AND SEARCH -->
        <div class="search-and-filters">
          <div class="fs-toggles">
            <div @click="toggleSearch" class="search-toggle-btn search-icon">
              <img v-if="searchedWatches.length > 0" class="center" src="/images/icons/loupe-gold.svg" />
              <img v-else class="center" src="/images/icons/loupe-blk.svg" />
            </div>
            <div @click="toggleFilters" class="filters-toggle-btn filter-icon">
              <img v-if="filtersActive" class="center" src="/images/icons/filters-active.svg" />
              <img v-else class="center" src="/images/icons/filters-blk.svg" />
            </div>
          </div>

          <!-- SEARCH TRAY -->
          <div :class="['search-tray', { open: showSearch }]">
            <SearchComponent
              :items="watches"
              :showSearch="showSearch"
              @search-results="handleSearchResults"
              @closeSearch="showSearch = false"
            />
            <a class="close-search" @click="toggleSearch">Close</a>

            <div v-if="searchedWatches.length" :class="['search-count', { alert: searchedWatches.length === 0 }]">
              {{ searchedWatches.length }} watch{{ searchedWatches.length === 1 ? '' : 'es' }} match{{ searchedWatches.length === 1 ? 'es' : '' }} your criteria.
            </div>

            <div v-if="searchedWatches.length > 0" class="list-grid-toggle view-icon" @click="toggleView">
              <img v-if="gridView" class="center" src="/images/icons/list-view-icon.svg" />
              <img v-else class="center" src="/images/icons/grid-view-icon.svg" />
            </div>

            <!-- SEARCH RESULTS -->
            <div class="search-results">
              <div v-if="gridView" class="grid-view">
                <span v-for="watch in searchedWatches" :key="watch.SKU">
                  <router-link :to="{ name: 'rmwatch', params: { watchSKU: watch.SKU, watchModel: watch.HTMLSafeModel.split(' ').join('') } }">
                    <figure class="image-container">
                      <img loading="lazy" class="imageGridSource" @error="imageLoadError" :src="alternativeImageGridSource(watch)" alt="" />
                      <figcaption>
                        <span>{{ watch.Model }}</span><br />
                        <span>Available in {{ watch.WatchLocation }}</span>
                      </figcaption>
                    </figure>
                  </router-link>
                </span>
              </div>

              <ul v-else class="list-view">
                <li v-for="watch in searchedWatches" :key="watch.SKU">
                  <div class="search-list-container">
                    <figure class="image-container">
                      <img loading="lazy" style="width: 150px;" class="imageGridSource" @error="imageLoadError" :src="alternativeImageGridSource(watch)" alt="" />
                      <figcaption>
                        <router-link :to="{ name: 'rmwatch', params: { watchSKU: watch.SKU, watchModel: watch.HTMLSafeModel.split(' ').join('') } }">
                          <h4>{{ watch.ManufacturerName }} {{ watch.Model }}</h4>
                        </router-link>
                        <p>Material: {{ watch.Material }}</p>
                        <p>Year: {{ watch.Year }}</p>
                        <p v-if="watch.StatusName === 'Sold'">Sold</p>
                        <p v-if="watch.StatusName === null || watch.StatusName === 'on hold'">In Stock</p>
                        <p v-if="watch.StatusName === 'deposit'">On Deposit</p>
                        <p v-else>
                          {{ watch.StatusName }}&nbsp;&nbsp;|&nbsp;
                          <span v-if="watch.isPOA" class="price">POA</span>
                          <span v-else class="price">{{ watch.currencySymbol }} {{ watch.displayPrice }}</span>
                        </p>
                        <p class="appointment-at-location">
                          <strong>Available</strong> for appointments at our
                          <router-link :to="{ path: '/locations', query: { location: watch.WatchLocation } }">{{ watch.WatchLocation }}</router-link>
                          boutique
                        </p>
                      </figcaption>
                      <button @click="openPopup(watch.SKU)">Enquire Now</button>
                    </figure>
                  </div>

                  <div v-if="activePopup === watch.SKU" class="popup-overlay" @click.self="closePopup">
                    <div class="popup">
                      <a class="close-btn" @click="closePopup">Close</a><br />
                      <h4>Enquiry about {{ watch.ManufacturerName }} {{ watch.Model }}</h4>
                      <EnquiryForm :Model="watch.Model" :SKU="watch.SKU" formOrigin="Richard Mille - Search" />
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- FILTERS TRAY -->
          <div :class="['filters-tray', { open: showFilters }]">
            <FiltersComponent
              ref="filtersComponent"
              :filterOptions="dynamicFilters"
              :filterCount="filteredWatches.length"
              @filters-active="handleFiltersActive"
              @filter-changed="handleFilterChange"
              @close-filters="toggleFilters"
            />
          </div>
        </div>

        <div v-if="filtersActive && !showFilters" class="filters-applied">
          ACTIVE FILTERS <br /><span>{{ displayAppliedFilters }}</span><br />
          <span class="buttons-inline">
            <button @click="passResetFilters">Clear Filters</button>
            <button @click="toggleFilters" class="filters-toggle-btn filter-icon">Refine Filters</button>
          </span>
        </div>

        <div v-for="(watchesChunk, index) in allWatches" :key="index">
          <div class="watches">
            <div class="grid-view array-chunk" v-if="filteredWatches.length">
              <div v-for="watch in watchesChunk" :key="watch.SKU" class="grid-item watch__card" ref="card">
                <div class="watch__card-content">
                  <router-link :to="{ name: 'rmwatch', params: { watchSKU: watch.SKU, watchModel: watch.HTMLSafeModel.split(' ').join('') } }">
                    <figure class="image-container">
                      <img loading="lazy" class="imageGridSource" :src="imageGridSource(watch)" @error="handleImagePlaceholder" alt="" />
                      <img loading="lazy" class="alternativeImageGridSource" @error="imageLoadError" :src="alternativeImageGridSource(watch)" alt="" />
                      <figcaption>
                        <p>{{ watch.ManufacturerName }} {{ watch.Model }}</p>
                        <!-- // added in 2026 update --> <p style="font-size: 0.7rem; padding-top: 4px;">{{ watch.Material }}</p> 
                      </figcaption>
                      <figcaption>
                        <span v-if="watch.StatusName === 'Sold'">Sold</span>
                        <span v-if="watch.StatusName === null || watch.StatusName === 'on hold'" class="status">In Stock</span>
                        <span v-if="watch.StatusName === 'deposit'" class="status">On Deposit</span>
                        <span v-else class="status">
                          {{ watch.StatusName }}&nbsp;&nbsp;|&nbsp;
                          <span v-if="watch.isPOA" class="price">POA</span>
                          <span v-else class="price">{{ watch.currencySymbol }} {{ watch.displayPrice }}
                             <br>
                            <span class="exc-taxes" v-if="watch.MarginScheme == '1'">&nbsp;exc.&ensp;taxes </span>
                          </span>
                         
                        </span>
                        <div class="learn-more">View <img class="center" src="/images/icons/ArrowRight.svg" /></div>
                      </figcaption>
                    </figure>
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <div class="featured-watches" v-if="[0,1,2,3].includes(index)">
            <FeaturedWatches :loop="index" :route="showRoute" />
          </div>
        </div>
      </div>
    </div>

    <!-- NO SEARCH RESULTS -->
    <div class="page-grid-wrapper">
      <div class="page-grid-fix">
        <div v-if="!filteredWatches.length && delayForm" class="no-search-results">
          <h2>Unfortunately we can’t find what you are looking for right now</h2>
          <p>Please Contact Us to Register Your Interest</p>
          <form>
            <ContactForm />
          </form>
        </div>
      </div>
    </div>

    <!-- SOLD WATCHES -->
    <div class="page-grid-wrapper sold-watches-grid">
      <div class="page-grid-fix" v-if="watchessold.length">
        <h2 style="padding: 25px 0; width: 100%; text-align: center;">Recently sold</h2>
        <div class="grid-view">
          <div v-for="watchsold in watchessold" :key="watchsold.SKU" class="grid-item watch__card" ref="card">
            <div class="watch__card-content">
              <router-link :to="{ name: 'rmwatch', params: { watchSKU: watchsold.SKU, watchModel: watchsold.HTMLSafeModel.split(' ').join('') } }">
                <figure>
                  <img loading="lazy" class="watch-image" :src="imageGridSource(watchsold)" alt="" @error="noPngInSold($event, watchsold)" />
                  <figcaption><p>{{ watchsold.Name }}</p></figcaption>
                  <figcaption><span class="status">Sold</span></figcaption>
                </figure>
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p>No watches sold data available.</p>
      </div>
    </div>
  </div>
</template>

<script>
import ContactForm from '../components/ContactForm.vue';
import FeaturedWatches from '../components/FeaturedWatches.vue'; 
import EnquiryForm from '../components/EnquiryForm.vue';
import FiltersComponent from '../components/FiltersComponent.vue';
import SearchComponent from '../components/SearchComponent.vue';
import axios from 'axios';
import _ from 'lodash';

export default {
  name: 'WatchesRandomComponent',
  components: { ContactForm, EnquiryForm, FeaturedWatches, FiltersComponent, SearchComponent },
  props: ['featuredRM', 'siteSettings'],
  data() {
    return {
      watches: [],
      watchessold: [],
      filteredWatches: [],
      searchedWatches: [],
      dynamicFilters: {},
      appliedFilters: {},
      showSearch: false,
      showFilters: false,
      filtersActive: false,
      gridView: false,
      activePopup: null,
      showRoute: this.$route.name,
      watchesRandomCancelToken: null,
      watchesSoldCancelToken: null,
      delayForm: false,
      routeChangeTimeout: null
    };
  },
  mounted() {
    this.getWatchesRandom();
    this.getWatchesSold();
    setTimeout(() => this.delayForm = true, 2000);
  },
  watch: {
    '$route.name'(newName) {
      this.showRoute = newName;

      if (this.watchesRandomCancelToken) this.watchesRandomCancelToken.cancel('Route changed.');
      if (this.watchesSoldCancelToken) this.watchesSoldCancelToken.cancel('Route changed.');

      clearTimeout(this.routeChangeTimeout);
      this.routeChangeTimeout = setTimeout(() => {
        this.getWatchesRandom();
        this.getWatchesSold();
      }, 200);
    }
  },
  methods: {
    async getWatchesRandom(address) {
      if (this.watchesRandomCancelToken) this.watchesRandomCancelToken.cancel('New request or route change.');
      this.watchesRandomCancelToken = axios.CancelToken.source();
      try {
        const response = await this.$axios.get(address || '/api/rmonly', {
          cancelToken: this.watchesRandomCancelToken.token
        });
        this.watches = this.shuffleArray(response.data.data);
        this.filteredWatches = this.watches;
        this.updateDynamicFilters();
        this.$emit("route-data-ready"); // hide loader
      } catch (err) {
        if (!axios.isCancel(err)) console.error(err);
      }
    },
    async getWatchesSold(address) {
      if (this.watchesSoldCancelToken) this.watchesSoldCancelToken.cancel('New request or route change.');
      this.watchesSoldCancelToken = axios.CancelToken.source();
      try {
        const response = await this.$axios.get(address || '/api/rmonlysold', {
          cancelToken: this.watchesSoldCancelToken.token
        });
        this.watchessold = response.data.data;
      } catch (err) {
        if (!axios.isCancel(err)) console.error(err);
      }
    },
    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    handleSearchResults(searchResults) { this.searchedWatches = searchResults; },
    toggleFilters() { if (this.showSearch) this.showSearch = false; this.showFilters = !this.showFilters; },
    toggleSearch() { if (this.showFilters) this.showFilters = false; this.showSearch = !this.showSearch; },
    toggleView() { this.gridView = !this.gridView; },
    openPopup(sku) { this.activePopup = sku; },
    closePopup() { this.activePopup = null; },
    passResetFilters() { if (this.$refs.filtersComponent) this.$refs.filtersComponent.resetFilters(); },
    handleFiltersActive(isActive) { this.filtersActive = isActive; },
    handleFilterChange(selectedFilters) {
      this.appliedFilters = selectedFilters;
      const { locations = [], models = [], movements = [], materials = [] } = selectedFilters;
      this.filteredWatches = this.watches.filter(watch =>
        (!locations.length || locations.includes(watch.WatchLocation)) &&
        (!models.length || models.includes(watch.Model)) &&
        (!movements.length || movements.includes(watch.Movement)) &&
        (!materials.length || materials.includes(watch.WatchMaterial))
      );
    },
    updateDynamicFilters() {
      this.dynamicFilters = {
        locations: [...new Set(this.watches.map(w => w.WatchLocation))],
        models: [...new Set(this.watches.map(w => w.Model))],
        movements: this.getUniqueMovements(),
        materials: [...new Set(this.watches.map(w => w.WatchMaterial))]
      };
    },
    getUniqueMovements() {
      const set = new Set();
      this.watches.forEach(w => {
        if (w.Movement) {
          const clean = this.mapMovement(w.Movement);
          if (clean) set.add(clean);
        }
      });
      return [...set];
    },
    mapMovement(m) {
      const map = { 'Tourbillon':'Tourbillon', 'Automatic':'Automatic', 'Manual Winding':'Manual', 'Manual':'Manual', 'Quartz':'Quartz', 'Mechanical':'Manual', 'Barometer':'Barometer' };
      if (m.includes('Tourbillon')) return map['Tourbillon'];
      if (m.includes('Automatic') || m.includes('RM Automatic') || m.includes('AP Calibre')) return map['Automatic'];
      if (m.includes('Manual') || m.includes('Manual Calibre') || m.includes('Mechanical')) return map['Manual'];
      if (m.includes('Quartz')) return map['Quartz'];
      if (m.includes('Barometer')) return map['Barometer'];
      return null;
    },
    imageLoadError(e) { e.target.parentNode.removeChild(e.target); },
    handleImagePlaceholder(e) { if (e.target.dataset.fallbackApplied) return; e.target.dataset.fallbackApplied=true; e.target.src='/watches/gallery/maintenance/grid_placeholder.png'; },
    imageGridSource(w) { return `/watches/gallery/NW-${w.SKU}/grid-image/grid_NW-${w.SKU}.png`; },
    alternativeImageGridSource(w) { return `/watches/gallery/NW-${w.SKU}/grid-image/custom_grid_image_NW-${w.SKU}.jpg`; },
    noPngInSold(e,w) { if(e.target.dataset.fallbackApplied) return; e.target.dataset.fallbackApplied=true; e.target.src=`/watches/gallery/NW-${w.SKU}/grid-image/grid_NW-${w.SKU}.jpg`; }
  },
  computed: {
    displayAppliedFilters() {
      return Object.values(this.appliedFilters).flat().join(' ');
    },
    allWatches() { return _.chunk(this.filteredWatches, 6); },
  },
  beforeUnmount() {
    if (this.watchesRandomCancelToken) this.watchesRandomCancelToken.cancel('Component unmounted.');
    if (this.watchesSoldCancelToken) this.watchesSoldCancelToken.cancel('Component unmounted.');
    clearTimeout(this.routeChangeTimeout);
  }
};
</script>
<style scoped>
/* figcaption p, figcaption span{
   color: white !important;
} */
 /* figcaption img {
  filter: invert(1%);
 } */
</style>